import { GovernmentIdType } from '@banking/api/models/onboarding/individual';
import { State, requiredIndividualsPresent } from './state';
import { Action, openDialogAction, closeDialogAction, updateIndividualsResponse, updateIndividualsError } from './action';
import { Address } from '@square/dashboard-protos/models/squareup/connect/v2/resources/address';
import { assertNonNullable } from '@banking/ui/utils/assert';
// This function ensures we get unique individual ids in a particular browser
// session.
let clientIdCounter = 0;
export function generateIndividualClientId() {
  return `new-individual-${clientIdCounter++}`;
}
export function newIndividual(context, {
  isManager = false,
  isSignificantOwner = false
}) {
  return {
    id: context.individualIdGenerator(),
    isManager,
    isSignificantOwner,
    governmentId: {
      type: GovernmentIdType.SSN
    },
    address: Address.create()
  };
}
function handleAddManagerButtonClick(state, context) {
  const individual = newIndividual(context, {
    isManager: true
  });
  return [state, () => openDialogAction(State.DialogScreen.AddManager, individual)];
}
function handleAddOwnerButtonClick(state, context) {
  const individual = newIndividual(context, {
    isSignificantOwner: true
  });
  return [state, () => openDialogAction(State.DialogScreen.AddOwner, individual)];
}
function handleCloseDialog(state) {
  return [{
    ...state,
    dialog: undefined
  }, null];
}
function handleContinueButtonClick(state) {
  if (requiredIndividualsPresent(state)) {
    return [{
      ...state,
      done: true
    }, null];
  } else {
    return [{
      ...state,
      showAddIndividualsValidation: true
    }, null];
  }
}
function handleDeleteIndividualSubmitRequest(state, action, context) {
  const work = () => {
    if (!action.individualId) {
      throw new Error('tried to emit a delete action with no individual');
    }
    return context.individualApi.delete(action.individualId).then(updateIndividualsResponse);
  };
  return [state, work];
}

// TODO:
// - Validations here or in dialog component?
function handleIndividualSubmitRequest(state, action, context) {
  const editing = state.dialog?.screen === State.DialogScreen.EditManager || state.dialog?.screen === State.DialogScreen.EditOwner;
  const work = () => {
    const rpc = editing ? context.individualApi.edit.bind(context.individualApi) : context.individualApi.add.bind(context.individualApi);
    return rpc(action.individual, [...state.individuals]).then(updateIndividualsResponse).catch(error => {
      if (error instanceof Error) {
        return null;
      } else {
        return updateIndividualsError(error);
      }
    });
  };
  return [state, work];
}
function handleEditIndividualClick(state, action) {
  const individual = state.individuals.find(individual => {
    return individual.id === action.individualId;
  });
  if (!individual) {
    throw new Error('failed to find individual to edit');
  }
  const screen = individual.isManager ? State.DialogScreen.EditManager : State.DialogScreen.EditOwner;
  return [state, () => openDialogAction(screen, individual)];
}
function handleInitialize(state, action) {
  const {
    requireManager,
    hasSignificantOwners,
    existingIndividuals,
    businessName
  } = action;
  return [{
    ...state,
    businessName,
    requireManager,
    hasSignificantOwners,
    individuals: existingIndividuals
  }, null];
}
function handleOpenDialog(state, action) {
  if (state.dialog) {
    throw new Error('cannot open dialog, one is already open');
  }
  const {
    screen,
    individual
  } = action;
  return [{
    ...state,
    dialog: {
      screen,
      individual
    }
  }, null];
}
function handleUpdateIndividualsResponse(state, action) {
  return [{
    ...state,
    individuals: action.newIndividuals
  }, closeDialogAction];
}
function handleUpdateIndividualsError(state, action) {
  const {
    screen,
    individual
  } = assertNonNullable(state.dialog);
  const errors = [...action.errors];
  return [{
    ...state,
    dialog: {
      screen,
      individual,
      errors
    }
  }, null];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case Action.Type.AddManagerButtonClick:
      return handleAddManagerButtonClick(state, context);
    case Action.Type.AddOwnerButtonClick:
      return handleAddOwnerButtonClick(state, context);
    case Action.Type.CloseDialog:
      return handleCloseDialog(state);
    case Action.Type.ContinueButtonClick:
      return handleContinueButtonClick(state);
    case Action.Type.DeleteIndividualSubmitRequest:
      return handleDeleteIndividualSubmitRequest(state, action, context);
    case Action.Type.IndividualSubmitRequest:
      return handleIndividualSubmitRequest(state, action, context);
    case Action.Type.EditIndividualClick:
      return handleEditIndividualClick(state, action);
    case Action.Type.CiInitialize:
      return handleInitialize(state, action);
    case Action.Type.OpenDialog:
      return handleOpenDialog(state, action);
    case Action.Type.UpdateIndividualsResponse:
      return handleUpdateIndividualsResponse(state, action);
    case Action.Type.UpdateIndividualsError:
      return handleUpdateIndividualsError(state, action);
  }
};